import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type { ActivityStatus, ActivityType, SubmissionStatus } from '@/types/activity';
import { ACTIVITY_PRIORITIES, ACTIVITY_TYPES } from './schema';

/**
 * How the console describes an activity.
 *
 * One source of truth for every label and tone, so a project reads the same on
 * the card, in the filter row and on the review screen (CLAUDE.md §3).
 */

export const ACTIVITY_TYPE_LABEL = {
  INDIVIDUAL_PROJECT: 'Individual project',
  GROUP_ACTIVITY: 'Group activity',
  LAB_PRACTICAL: 'Lab practical',
  PRESENTATION: 'Presentation',
  FIELD_RESEARCH: 'Field research',
  ART_CRAFT: 'Art & craft',
} as const satisfies Record<ActivityType, string>;

export const ACTIVITY_TYPE_HINT = {
  INDIVIDUAL_PROJECT: 'One child, one model or report.',
  GROUP_ACTIVITY: 'A team piece, prepared and handed in together.',
  LAB_PRACTICAL: 'Done at the bench and written up in a lab record.',
  PRESENTATION: 'Stood up and delivered in front of the class.',
  FIELD_RESEARCH: 'Gathered outside the classroom — a survey, a site visit.',
  ART_CRAFT: 'Drawing, craft or chart work.',
} as const satisfies Record<ActivityType, string>;

export const ACTIVITY_TYPE_TONE = {
  INDIVIDUAL_PROJECT: 'accent',
  GROUP_ACTIVITY: 'info',
  LAB_PRACTICAL: 'success',
  PRESENTATION: 'warning',
  FIELD_RESEARCH: 'neutral',
  ART_CRAFT: 'accent',
} as const satisfies Record<ActivityType, BadgeTone>;

export const ACTIVITY_TYPE_OPTIONS: Option<ActivityType>[] = ACTIVITY_TYPES.map((value) => ({
  value,
  label: ACTIVITY_TYPE_LABEL[value],
}));

/**
 * How urgent an activity is, in the words a teacher and a parent both read.
 *
 * The same three words appear in the parent panel's Tasks table, which is the
 * whole point of the field — one vocabulary, so a teacher marking something
 * "High" and a family reading "High" mean the same thing.
 */
export const ACTIVITY_PRIORITY_LABEL = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
} as const satisfies Record<(typeof ACTIVITY_PRIORITIES)[number], string>;

export const ACTIVITY_PRIORITY_HINT: Record<(typeof ACTIVITY_PRIORITIES)[number], string> = {
  HIGH: 'Families should do this one first.',
  MEDIUM: 'Ordinary — due when it is due.',
  LOW: 'No hurry; whenever there is time.',
};

export const ACTIVITY_PRIORITY_OPTIONS: Option<(typeof ACTIVITY_PRIORITIES)[number]>[] =
  ACTIVITY_PRIORITIES.map((value) => ({ value, label: ACTIVITY_PRIORITY_LABEL[value] }));

export const ACTIVITY_TYPE_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'All kinds' },
  ...ACTIVITY_TYPE_OPTIONS,
];

/** Derived from the due date on the server, so these only render it. */
export const ACTIVITY_STATUS_LABEL = {
  OPEN: 'Open',
  CLOSED: 'Past due',
} as const satisfies Record<ActivityStatus, string>;

export const ACTIVITY_STATUS_TONE = {
  OPEN: 'success',
  CLOSED: 'neutral',
} as const satisfies Record<ActivityStatus, BadgeTone>;

export const ACTIVITY_STATUS_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'Open and past due' },
  { value: 'OPEN', label: ACTIVITY_STATUS_LABEL.OPEN },
  { value: 'CLOSED', label: ACTIVITY_STATUS_LABEL.CLOSED },
];

/** Where one child has got to, as the review table shows it. */
export const SUBMISSION_STATUS_LABEL = {
  PENDING: 'Not handed in',
  SUBMITTED: 'Handed in',
  GRADED: 'Marked',
} as const satisfies Record<SubmissionStatus, string>;

export const SUBMISSION_STATUS_TONE = {
  PENDING: 'neutral',
  SUBMITTED: 'info',
  GRADED: 'success',
} as const satisfies Record<SubmissionStatus, BadgeTone>;

export const SUBMISSION_STATUS_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'Everyone' },
  { value: 'PENDING', label: SUBMISSION_STATUS_LABEL.PENDING },
  { value: 'SUBMITTED', label: SUBMISSION_STATUS_LABEL.SUBMITTED },
  { value: 'GRADED', label: SUBMISSION_STATUS_LABEL.GRADED },
];
